import {
  Handshake,
  RequestQuote,
  Receipt,
  Savings,
  Business,
  Approval,
  Checklist,
  Payment,
  AccountBalanceWallet,
  MonetizationOn,
  NoAccounts,
  Schema
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import SidebarIcon from 'components/Sidebar/SidebarIcon/SidebarIcon';
import { SidebarTab } from 'components/Sidebar/SidebarTab';

import { useCompanies } from 'context/CompanyContext';
import { useTranslations } from 'context/TranslationContext';
import { useGuardedNavigate } from 'context/UnsavedChangesContext';

import { useRouteSecurity } from 'hooks/useRouteSecurity';

import { AppRoutesEnum } from 'utils/routes';

import {
  sidebarIconStyle,
  sidebarIconStyleSelected,
  sidebarIconStyleSmall
} from 'styles/components/SidebarStyle';

import dashboardIcon from 'assets/images/ic_dashboard.svg';
import invoicesIcon from 'assets/images/ic_invoice.svg';

import { getSidebarTabs } from './SidebarHelper';

interface SidebarTabsListProps {
  currentCompanyId: string;
  isSidebarExpanded: boolean;
  onTabClick?: () => void;
}

const tabIcons = {
  [AppRoutesEnum.DASHBOARD as string]: (isSelected: boolean) => (
    <SidebarIcon iconSource={dashboardIcon} shouldBeSelected={isSelected} />
  ),
  [AppRoutesEnum.INVOICES]: (isSelected: boolean) => (
    <SidebarIcon iconSource={invoicesIcon} shouldBeSelected={isSelected} />
  ),
  [AppRoutesEnum.PROMPT_BASED_REPORTING]: (isSelected: boolean) => (
    <Schema
      sx={{
        ...sidebarIconStyleSmall,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.INVOICES_ACCOUNTING]: (isSelected: boolean) => (
    <AccountBalanceWallet
      sx={{
        ...sidebarIconStyleSmall,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.INVOICES_APPROVAL]: (isSelected: boolean) => (
    <Checklist
      sx={{
        ...sidebarIconStyleSmall,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.INVOICES_PAYMENT]: (isSelected: boolean) => (
    <Payment
      sx={{
        ...sidebarIconStyleSmall,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.COMPANY_COUNTERPARTIES]: (isSelected: boolean) => (
    <Handshake
      sx={{
        ...sidebarIconStyle,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.COMPANY_COUNTERPARTIES_NOT_VALIDATED]: (
    isSelected: boolean
  ) => (
    <NoAccounts
      sx={{
        ...sidebarIconStyleSmall,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.COMPANY_PAYROLL]: (isSelected: boolean) => (
    <RequestQuote
      sx={{
        ...sidebarIconStyle,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.COMPANY_EXPENSE_TYPES]: (isSelected: boolean) => (
    <Receipt
      sx={{
        ...sidebarIconStyle,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.COMPANY_APPROVAL_RULES]: (isSelected: boolean) => (
    <Approval
      sx={{
        ...sidebarIconStyle,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.COMPANY_BUDGETING]: (isSelected: boolean) => (
    <Savings
      sx={{
        ...sidebarIconStyle,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.COMPANY_DETAILS]: (isSelected: boolean) => (
    <Business
      sx={{
        ...sidebarIconStyle,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  ),
  [AppRoutesEnum.REVENUE]: (isSelected: boolean) => (
    <MonetizationOn
      sx={{
        ...sidebarIconStyle,
        ...(isSelected ? sidebarIconStyleSelected : {})
      }}
    />
  )
};

export const SidebarTabsList = ({
  currentCompanyId,
  isSidebarExpanded,
  onTabClick
}: SidebarTabsListProps) => {
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const { translate } = useTranslations();
  const { checkRoutePermission } = useRouteSecurity();
  const companies = useCompanies();

  const sidebarTabs = useMemo(() => {
    return getSidebarTabs({
      currentCompanyId,
      companies: companies.companiesList,
      translate,
      navigate,
      currentLocation: location.pathname,
      checkRoutePermission
    }).map((tab) => ({
      ...tab,
      tabIcon: tabIcons[tab.route](tab.isSelected)
    }));
  }, [
    currentCompanyId,
    companies,
    translate,
    location.pathname,
    checkRoutePermission,
    navigate
  ]);

  return companies.companiesList.length ? (
    <Box>
      {sidebarTabs.map((tab) => (
        <SidebarTab
          key={tab.tabText}
          onClick={() => {
            tab.onClick();
            if (onTabClick) {
              onTabClick();
            }
          }}
          text={tab.tabText}
          isSelected={tab.isSelected}
          tabIcon={tab.tabIcon}
          isSidebarExpanded={isSidebarExpanded}
          tabFilterType={tab.filterType}
          isNested={tab.isNested}
          tabLongText={tab.tabLongText}
        />
      ))}
    </Box>
  ) : null;
};
